/**
 * LEFT-SIDE-ONLY REVERSAL ODDS for a CTS point.
 *
 * <h2>"No look-ahead" has TWO halves, and the second is the one that gets missed</h2>
 *
 * <pre>
 *   1. a precedent must have FIRED before t                 p.index < t
 *   2. its OUTCOME must already be known at t               p.index + horizon < t
 * </pre>
 *
 * The first is obvious and the second is the trap. A precedent three bars back with a ten-bar
 * horizon resolves seven bars into t's future — so counting it means the estimate at t is built
 * partly from price action nobody standing at t could have seen. The leak is invisible in every
 * number it produces: the odds look reasonable, the sample size looks healthy, and the series
 * quietly knows things it should not.
 *
 * <p>So a precedent enters the tally only when the bar that resolved it is itself behind t.
 * `theOutcomeMustBeKnownToo` in the test file is the control that holds it.
 *
 * <h2>THE WEIGHTS ARE MEASURED, NOT TYPED — and that is a deviation worth stating</h2>
 *
 * The brief quotes the empirical rules with figures: Zone 1 long at +22.7%, an agreeing RSI
 * divergence at +18.8 points. Those are real and this repository measured them — on a scan of
 * the WHOLE history. Hardcoding them here would import full-sample knowledge into every bar,
 * including 1995, where neither had been observed yet. That is precisely the look-ahead this
 * feature exists to avoid, arriving through the constants rather than through the data.
 *
 * <p>The brief's own wording settles it: "our validated empirical rules LEARNED UP TO BAR t". So
 * every adjustment below is re-measured from the precedents available at t, and the quoted
 * figures are the EXPECTED CONVERGENCE at the right-hand edge rather than inputs — where the
 * left-only window is almost the whole history, the two should agree, and a test checks that.
 *
 * <h2>The combination is ADDITIVE, which is an approximation</h2>
 *
 * Four adjustments are summed, and they share variance: a Zone 3 signal is usually also a
 * far-from-equator signal, so both adjustments partly describe the same thing and the total
 * over-counts. Each is clamped and the result is clamped into a probability, which bounds the
 * error without pretending it is absent. A logistic model fitted left-only at every bar would
 * be the principled answer and is far more machinery than a tooltip warrants; the limitation is
 * stated in the tooltip's own breakdown line rather than hidden.
 */

import { detectPivots, PIVOT_HIGH, PIVOT_LOW, DEFAULT_PIVOT_STRENGTH } from './pivots';
import { BAR_CLOSE, heightsFromEquator, zoneOf } from './analyticaGeometry';
import { DEFAULT_HORIZON, DISTANCE_EDGES, DISTANCE_LABELS } from './analyticaCts';
import { wilson } from './analyticaStats';

/** The brief's own threshold: below this many resolved precedents, the tooltip declines. */
export const MIN_SAMPLE = 10;

/**
 * A sub-cohort smaller than this contributes NOTHING rather than a noisy adjustment.
 *
 * <p>The same number as the base threshold, deliberately: a cohort the engine would refuse to
 * quote an odds figure from should not be trusted to MOVE one either.
 */
export const MIN_ADJUSTMENT_SAMPLE = 10;

/** No single feature may move the estimate by more than this, for the shared-variance reason. */
export const MAX_ADJUSTMENT = 0.25;

/**
 * And no COMBINATION may move it by more than this — which is a separate cap and is needed.
 *
 * <p>Found on live data: with four features agreeing, a 75% base produced **~98% odds**, which
 * is the final probability clamp binding rather than a measurement. Near-certainty from a
 * tooltip is the worst thing this feature could print, and it is exactly what an additive
 * combination of correlated evidence manufactures — a Zone 3 signal is usually also a
 * far-from-equator signal, so two of the four adjustments are largely describing one fact and
 * the sum counts it twice.
 *
 * <p>0.30 rather than 4 x 0.25: several agreeing features should move the estimate further than
 * one does, and nothing like four times as far. The number is a judgement and is stated as one;
 * what is not a judgement is that the uncapped sum was producing figures the sample cannot
 * support.
 */
export const MAX_TOTAL_ADJUSTMENT = 0.30;

/** A probability is never 0 or 1 from a finite sample. */
export const ODDS_FLOOR = 0.02;
export const ODDS_CEILING = 0.98;

/** How far back an RSI divergence may sit and still count as present at the signal. */
export const DIVERGENCE_LOOKBACK = 5;

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * THE HEADROOM SCALE — why an adjustment cannot be applied additively near an extreme.
 *
 * <p>Measured on live SPY: a 1990s long signal had a left-only base rate of **86% over 42 prior
 * resolved setups** and a Distribution sub-cohort measuring 14 points better, so the arithmetic
 * gave 100% and the card printed **98%** — which is {@link #ODDS_CEILING}, a bound, presented as
 * a measurement. The total cap above does not help: only ONE adjustment was applied.
 *
 * <p>The error is a unit error rather than a size error. A difference of 14 percentage points
 * measured between two cohorts cannot mean the same thing against a 50% base and an 86% one:
 * at 86% there are only 12 points of room left, so applying it additively hands the final clamp
 * the job of deciding the answer. The adjustment is therefore scaled by the room remaining in
 * the direction it points, as a fraction of the room a 50% base would have had — which is the
 * log-odds behaviour (an adjustment has diminishing absolute effect as certainty grows) written
 * as one expression in probability space, where the measurements themselves live.
 *
 * <p>At a 50% base nothing changes. At 86% a +14 becomes about +3.5. And since the summed
 * adjustment is capped below the full span, **the ceiling can no longer bind at all** — it goes
 * back to being the safety net it was meant to be rather than a reporting mechanism.
 */
function headroomScaled(baseRate, total) {
  if (total === 0) return 0;
  const room = total > 0 ? ODDS_CEILING - baseRate : baseRate - ODDS_FLOOR;
  const atEven = total > 0 ? ODDS_CEILING - 0.5 : 0.5 - ODDS_FLOOR;
  return total * clamp(room / atEven, 0, 1);
}

/**
 * The 50/200 regime at each bar, left-only by construction.
 *
 * <p>UNKNOWN until both averages exist — the same rule the macro gate states, and for the same
 * reason: before 200 bars the quantity does not exist, and a guess there would be a regime
 * label on the warm-up.
 */
export function regimeSeries(ohlc, fast = 50, slow = 200) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const out = new Array(bars.length).fill('UNKNOWN');
  let sumFast = 0;
  let sumSlow = 0;
  for (let i = 0; i < bars.length; i++) {
    const c = n(bars[i]?.[BAR_CLOSE]);
    if (c === null) continue;
    sumFast += c;
    sumSlow += c;
    if (i >= fast) sumFast -= n(bars[i - fast]?.[BAR_CLOSE]) || 0;
    if (i >= slow) sumSlow -= n(bars[i - slow]?.[BAR_CLOSE]) || 0;
    if (i + 1 < slow) continue;
    const f = sumFast / fast;
    const s = sumSlow / slow;
    out[i] = f > s ? 'ACCUMULATION' : f < s ? 'DISTRIBUTION' : 'NEUTRAL';
  }
  return out;
}

/**
 * Divergences, keyed by the bar at which each becomes KNOWABLE.
 *
 * <h2>Backward-only and confirmation-aware, unlike dimension 7</h2>
 *
 * Chart Analytica's divergence dimension uses a symmetric window either side of a point, which
 * is right for a retrospective audit: a pivot is confirmed `strength` bars late, so a divergence
 * that "belongs" to a signal can be dated after it. For a LEFT-ONLY estimate that is a leak —
 * a divergence whose second pivot sits at or after t is not something a reader at t could know.
 *
 * <p>So a divergence at pivot `d` is available from bar `d + strength` onward (when its own
 * right-hand bars have printed) and counts for a signal at t only while `t - d <= lookback`.
 *
 * @returns {Map<number, 'BULLISH'|'BEARISH'>} bar index -> the sense available at that bar
 */
export function knownDivergences(ohlc, rsi, strength = DEFAULT_PIVOT_STRENGTH,
  lookback = DIVERGENCE_LOOKBACK) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const series = Array.isArray(rsi) ? rsi : [];
  const at = new Map();
  if (bars.length === 0 || series.length === 0) return at;

  const { all } = detectPivots(bars, strength);
  [PIVOT_HIGH, PIVOT_LOW].forEach((type) => {
    const same = all.filter(p => p.type === type);
    for (let i = 1; i < same.length; i++) {
      const a = same[i - 1];
      const b = same[i];
      const ra = n(series[a.index]);
      const rb = n(series[b.index]);
      if (ra === null || rb === null) continue;
      let sense = null;
      if (type === PIVOT_HIGH && b.price > a.price && rb < ra) sense = 'BEARISH';
      if (type === PIVOT_LOW && b.price < a.price && rb > ra) sense = 'BULLISH';
      if (!sense) continue;
      // Available only once the pivot is confirmed, and only for `lookback` bars after that.
      const from = b.index + strength;
      for (let k = from; k <= b.index + lookback; k++) {
        if (k >= 0 && !at.has(k)) at.set(k, sense);
      }
    }
  });
  return at;
}

/**
 * The feature vector for one CTS point, every field knowable at its own bar.
 *
 * @returns {object|null} null when the point is not on the axis
 */
function featuresFor(point, index, ohlc, sectionByBar, regimes, divergences, horizon, last) {
  const close = n(ohlc[index]?.[BAR_CLOSE]);
  if (close === null) return null;
  /*
   * PAST THE REPLAY BOUNDARY, this point does not exist yet.
   *
   * `last` is the end of what the reader can SEE, not the end of the series — so in a replay a
   * point ahead of the playhead is returned un-assessed and carries no features at all. Not
   * even its regime or its distance: both are computed from bars the reader has not been
   * shown, and a card quoting them would be the leak the whole replay exists to prevent.
   */
  if (index > last) {
    return {
      index,
      date: point.date,
      /*
       * NOT EVEN THE DIRECTION.
       *
       * The caller derives it from where the scan placed the marker relative to price, so it
       * is a reading of the very bar the replay is hiding. A card that printed "LONG" here
       * would be telling the reader which way an undrawn bar turned — the smallest and most
       * plausible-looking leak available, and the one a reader would never think to doubt.
       */
      direction: null,
      close: null,
      beyondBoundary: true,
      resolved: false,
      success: null,
    };
  }
  const direction = point.direction === 'down' ? 'down' : 'up';
  const section = sectionByBar?.[index] || null;
  const distance = section ? heightsFromEquator(close, section) : null;
  const abs = distance === null ? null : Math.abs(distance);
  const divergence = divergences.get(index) || null;

  // Resolved against the FULL series, because this is a precedent's outcome — it is admitted to
  // a tally only once the bar that resolved it is behind the point being estimated.
  const resolvedAt = index + horizon;
  const resolved = resolvedAt <= last;
  const future = resolved ? n(ohlc[resolvedAt]?.[BAR_CLOSE]) : null;

  return {
    index,
    date: point.date,
    direction,
    close,
    zone: section ? zoneOf(close, section) : null,
    category: section ? section.category : null,
    distance,
    absDistance: abs,
    distanceBucket: abs === null ? null : bucketLabel(abs),
    regime: regimes[index] || 'UNKNOWN',
    divergence,
    divergenceAgrees: divergence ? ((divergence === 'BULLISH') === (direction === 'up')) : false,
    resolvedAt,
    resolved,
    success: resolved && future !== null
      ? (direction === 'up' ? future > close : future < close)
      : null,
  };
}

function bucketLabel(abs) {
  for (let i = 0; i < DISTANCE_EDGES.length; i++) {
    if (abs < DISTANCE_EDGES[i]) return DISTANCE_LABELS[i];
  }
  return DISTANCE_LABELS[DISTANCE_LABELS.length - 1];
}

/**
 * Left-only odds for every CTS point on the chart.
 *
 * <p>ONE forward pass. Precedents are admitted to the tallies through a pointer that only
 * advances once a precedent's resolving bar is behind the point being estimated, so the
 * two-part no-look-ahead rule is a property of the loop rather than a filter somebody has to
 * remember to apply.
 *
 * @param {object} input
 * @param {Array<string>} input.dates  the chart's own axis
 * @param {Array<number[]>} input.ohlc ECharts order: [open, close, low, high]
 * @param {Array<number>} input.rsi
 * @param {Array<object|null>} input.sectionByBar which channel governs each bar, or null
 * @param {Array<object>} input.points `{date, direction}` read off the CTS overlay
 * @param {number} [input.upToIndex] THE REPLAY BOUNDARY — the last bar the reader can see.
 *        Defaults to the end of the series, which is the ordinary chart. See below.
 * @returns {Array<object>} one record per point, in bar order
 */
export function ctsOddsSeries({
  dates = [], ohlc = [], rsi = [], sectionByBar = [], points = [],
  horizon = DEFAULT_HORIZON, pivotStrength = DEFAULT_PIVOT_STRENGTH,
  minSample = MIN_SAMPLE, upToIndex = null,
} = {}) {
  const axis = Array.isArray(dates) ? dates : [];
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const h = Math.max(1, Math.floor(n(horizon) || DEFAULT_HORIZON));
  /*
   * THE BOUNDARY DOES TWO THINGS, and the second is the one that gets missed.
   *
   * It stops a point AHEAD of the playhead being assessed — obvious, and handled in
   * `featuresFor`. It also decides whether an EARLIER point's outcome is knowable: a precedent
   * at t whose horizon ends past the boundary has not resolved from the reader's seat, however
   * long ago it fired, so it must not be admitted to a tally. Both come from one `last`.
   */
  const bound = Number.isFinite(upToIndex) ? Math.floor(upToIndex) : bars.length - 1;
  const last = Math.max(-1, Math.min(bars.length - 1, bound));
  if (axis.length === 0 || bars.length === 0) return [];

  const at = new Map();
  axis.forEach((d, i) => { if (!at.has(d)) at.set(d, i); });
  const regimes = regimeSeries(bars);
  const divergences = knownDivergences(bars, rsi, pivotStrength);

  const rows = (Array.isArray(points) ? points : [])
    .map((p) => {
      if (!p || typeof p.date !== 'string' || !at.has(p.date)) return null;
      return featuresFor(p, at.get(p.date), bars, sectionByBar, regimes, divergences, h, last);
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);

  // ── the expanding tallies ───────────────────────────────────────────────────────────────
  const tally = new Map();
  const bump = (key, success) => {
    const cur = tally.get(key) || { hits: 0, total: 0 };
    cur.total += 1;
    if (success) cur.hits += 1;
    tally.set(key, cur);
  };
  const rate = (key) => {
    const cur = tally.get(key);
    if (!cur || cur.total === 0) return { rate: null, n: 0 };
    return { rate: cur.hits / cur.total, n: cur.total };
  };
  const admit = (row) => {
    const d = row.direction;
    bump(d, row.success);
    if (row.zone) bump(`${d}|zone:${row.zone}`, row.success);
    if (row.distanceBucket) bump(`${d}|dist:${row.distanceBucket}`, row.success);
    bump(`${d}|regime:${row.regime}`, row.success);
    bump(`${d}|div:${row.divergenceAgrees ? 'yes' : 'no'}`, row.success);
  };

  // Only RESOLVED precedents can be admitted at all — an unresolved one has no outcome to
  // count, and waiting for it is the whole point of the pointer below.
  const pending = rows.filter(r => r.resolved);
  let next = 0;

  return rows.map((row) => {
    // THE TWO-PART RULE, as one condition: fired before t, AND resolved before t.
    while (next < pending.length && pending[next].resolvedAt < row.index) {
      admit(pending[next]);
      next += 1;
    }
    return estimate(row, rate, minSample, h);
  });
}

/** Turns the tallies standing at one point into an odds record. */
function estimate(row, rate, minSample, horizon) {
  if (row.beyondBoundary) {
    return {
      ...row,
      horizon,
      sample: 0,
      minSample,
      baseRate: null,
      baseInterval: null,
      odds: null,
      adjustments: [],
      insufficient: true,
      // Its OWN reason, not the cold-start one. "No history yet" and "this bar has not
      // happened yet on your screen" are different facts, and a reader stepping a replay
      // forward needs to know which one they are looking at.
      reason: 'Beyond the replay boundary — not assessed yet',
    };
  }
  const d = row.direction;
  const base = rate(d);
  const common = {
    ...row,
    horizon,
    sample: base.n,
    // CARRIED, so the tooltip can say "need 10" without holding its own copy of the threshold.
    // Two declarations of one number is how the message comes to disagree with the rule.
    minSample,
    baseRate: base.rate,
    baseInterval: base.n > 0 ? wilson(Math.round(base.rate * base.n), base.n) : null,
  };

  if (base.rate === null || base.n < minSample) {
    return {
      ...common,
      odds: null,
      adjustments: [],
      insufficient: true,
      // The brief's own wording, so the tooltip and the engine cannot drift apart about it.
      reason: 'Insufficient prior history for statistical odds',
    };
  }

  const adj = (key, label, cohort) => {
    if (!cohort) return { key, label, delta: null, n: 0, applied: false };
    const r = rate(`${d}|${cohort}`);
    if (r.rate === null || r.n < MIN_ADJUSTMENT_SAMPLE) {
      return { key, label, delta: null, n: r.n, applied: false };
    }
    return {
      key,
      label,
      delta: clamp(r.rate - base.rate, -MAX_ADJUSTMENT, MAX_ADJUSTMENT),
      n: r.n,
      applied: true,
    };
  };

  const adjustments = [
    adj('zone', row.zone ? row.zone.replace('_', ' ') : 'zone', row.zone && `zone:${row.zone}`),
    adj('distance', row.distanceBucket || 'distance',
      row.distanceBucket && `dist:${row.distanceBucket}`),
    adj('regime', row.regime, `regime:${row.regime}`),
    adj('divergence', row.divergenceAgrees ? 'divergence' : 'no divergence',
      `div:${row.divergenceAgrees ? 'yes' : 'no'}`),
  ];

  // THREE figures, in this order, and each is a different claim:
  //   summed   what the cohorts measured, added up
  //   total    after the correlated-evidence cap
  //   applied  after the headroom scale, which is what actually moves the odds
  const summed = adjustments.reduce((a, x) => a + (x.applied ? x.delta : 0), 0);
  const total = clamp(summed, -MAX_TOTAL_ADJUSTMENT, MAX_TOTAL_ADJUSTMENT);
  const applied = headroomScaled(base.rate, total);
  return {
    ...common,
    odds: clamp(base.rate + applied, ODDS_FLOOR, ODDS_CEILING),
    adjustments,
    // All three are carried so the tooltip can SAY what happened rather than printing a figure
    // that silently disagrees with the breakdown beside it — adjustments reading +12 +9 +8 +6
    // against a 75% base and an answer of 95% would look like an arithmetic error.
    adjustmentSum: summed,
    adjustmentTotal: total,
    adjustmentApplied: applied,
    capped: Math.abs(summed - total) > 1e-9,
    scaled: Math.abs(total - applied) > 5e-4,
    insufficient: false,
    reason: null,
  };
}

export default ctsOddsSeries;
